/**
 * "What applies here?" — the engine's answer turned into the panel's
 * model (CU-869fkbjdh § 3.6).
 *
 * The verdict is the engine's (`POST /setup/price-limits/resolve`, proxied
 * to `POST /jobs/limits/resolve`: `ranked → resolve_first → protections →
 * compose`, the same functions the sweep runs). Nothing is computed here:
 * this module only says, in the operator's words, what the engine said —
 * which rule it used, why the others did not apply (`why_not`, with a
 * dictionary and a fallback so a code the screen does not know still
 * reads as a sentence, never raw), the formula and the rows to light up.
 *
 * Pure: no React, no fetch. `node --test`.
 */
import { addDays, fmtDate, fmtDateShort, fmtMoney, isBinding, whereChips, whenLines } from './priceLimitsProjection.js'

const S = (v) => (v == null ? null : String(v))

/**
 * `ctx = { labels, matrixById, rulesById, judged }`:
 *   - `labels` as `whereChips` takes them;
 *   - `matrixById`, `rulesById`: `Map(String(id) → row)` of what the tab holds,
 *     so each line reads as the list does (the engine answers ids);
 *   - `judged`: per offer-class axis, what the cell was judged for —
 *     `{ franchise: { asked, id, label }, tariff: { asked, value, label },
 *     insurance: { asked, id, label } }`; `asked` is false when no rule uses
 *     the axis (nothing was asked, null travelled).
 *
 * Returns `{ floor, floorKind, floorLabel, ceiling, cell, judged, ordinary,
 * protections, formula, sentence, usedIds, litIds }`.
 */
export function fromEngine(res, ctx = {}) {
	const labels = ctx.labels || {}
	const matrixById = ctx.matrixById || new Map()
	const rulesById = ctx.rulesById || new Map()
	const judged = ctx.judged || {}
	const cell = res?.cell || {}
	const pickup = cell.pickup_date || null
	const lor = cell.lor == null ? null : Number(cell.lor)
	// The last day the rental is on, from the engine's own `lor_max` — not
	// recomputed from the typed days: the engine caps it at 30.
	const lorMax = cell.lor_max == null ? lor : Number(cell.lor_max)
	const lastDay = pickup && lorMax ? addDays(pickup, lorMax - 1) : null
	const acriss = cell.acriss || ''

	const usedIds = new Set((res?.used_rule_ids || []).map(S))
	const litIds = new Set()

	const name = (r, fallbackLabel) => {
		if (!r) return fallbackLabel ? `“${fallbackLabel}”` : 'a rule no longer in the list'
		const chips = whereChips(r, labels).map(c => c.text)
		const where = chips.length ? chips.join(' · ') : 'Everywhere'
		const w = whenLines(r)
		if (r.protects_dates) {
			return `${r.name || 'Protection'} · ${w.dates.replace(/^Any rental up to \d+ days /, '')} · ${where}`
		}
		const m = isBinding(r) ? matrixById.get(S(r.matrix_id)) : null
		return `${where}${m ? ` · matrix ${m.name}` : ''} · ${w.dates === 'Any date' ? 'any date' : `pickup ${w.dates}`}`
	}

	const ordinary = (res?.ordinary || []).map(e => {
		const id = S(e.id)
		const r = rulesById.get(id) || null
		const used = usedIds.has(id) || !!e.used
		if (e.applies) litIds.add(id)
		return {
			id, used, applies: !!e.applies,
			text: name(r, e.label),
			note: used ? 'the most specific match' : whyNotText(e.why_not, { r, acriss, lor, judged, label: e.label, office: cell.office_code }),
			amount: e.floor == null ? null : Number(e.floor),
		}
	})

	const protections = (res?.protections || []).map(e => {
		const id = S(e.id)
		const r = rulesById.get(id) || null
		const used = usedIds.has(id)
		if (e.crosses) litIds.add(id)
		let note
		if (e.crosses && e.applies && !e.outranked_by) {
			note = `Rental ${fmtDateShort(pickup, lastDay)} → ${fmtDateShort(lastDay, pickup)} crosses it`
		} else if (e.crosses && e.outranked_by === 'floor') note = 'crossed, but the ordinary floor is higher'
		else if (e.crosses && e.outranked_by === 'protection') note = 'crossed, but a higher protection wins'
		else if (e.crosses && e.why_not === 'no_band') note = 'crossed, but sets no floor here'
		else if (e.crosses) note = 'crossed'
		else if (e.why_not === 'dates') note = datesNote(e, pickup, lastDay)
		else note = whyNotText(e.why_not, { r, acriss, lor, judged, label: e.label, office: cell.office_code })
		return {
			id, used, applies: !!e.applies, crosses: !!e.crosses,
			text: name(r, e.label),
			note,
			amount: e.min == null ? null : Number(e.min),
		}
	})

	const floor = res?.floor?.value == null ? null : Number(res.floor.value)
	const floorKind = res?.floor?.kind || null
	const floorLabel = res?.floor?.label || null
	const ceiling = res?.ceiling == null ? null : Number(res.ceiling)
	const comp = res?.composition || {}
	const a = comp.ordinary_floor == null ? null : Number(comp.ordinary_floor)
	const b = comp.protection_floor == null ? null : Number(comp.protection_floor)
	const formula = floor == null
		? 'No floor applies to this cell'
		: a != null && b != null
			? `Floor = max(${fmtMoney(a)}, ${fmtMoney(b)}) = ${fmtMoney(floor)} €/day`
			: `Floor = ${fmtMoney(floor)} €/day`

	let sentence
	if (floor == null) sentence = 'No rule matches this cell — the strategy is free to price it.'
	else if (floorKind === 'date_protection') {
		const winner = protections.find(p => p.id === S(res.floor.price_limit_id))
		const src = res.protections?.find(p => S(p.id) === S(res.floor.price_limit_id))
		const range = src?.date_from && src?.date_to ? `${fmtDateShort(src.date_from, src.date_to)} → ${fmtDate(src.date_to)}` : 'its dates'
		sentence = `Rental ${fmtDateShort(pickup, lastDay)} → ${fmtDateShort(lastDay, pickup)} crosses ${range}, so the whole rental is floored at ${fmtMoney(floor)}${winner ? '' : ''}`
	} else if (floorLabel) sentence = `The matrix prices ${acriss} at ${lor} days`
	else sentence = 'The most specific ordinary rule for this pickup date'

	return {
		floor, floorKind, floorLabel, ceiling,
		cell: { office: cell.office_code || '', acriss, pickup, lor, lorMax, lastDay },
		judged: judgedSentences(judged),
		ordinary, protections, formula, sentence, usedIds, litIds,
	}
}

/** One sentence per offer-class axis that was asked (criterion 18); nothing for an axis no rule uses. */
export function judgedSentences(judged = {}) {
	const out = []
	const f = judged.franchise
	if (f?.asked) out.push(f.id != null ? `Judged for the own row of ${f.label || 'the franchise'}` : 'Judged for another franchise')
	const t = judged.tariff
	if (t?.asked) out.push(t.value != null ? `Judged for ${t.label || (t.value ? 'Prepaid' : 'Non prepaid')}` : 'Judged for another tariff type')
	const i = judged.insurance
	if (i?.asked) out.push(i.id != null ? `Judged for ${i.label || 'the insurance'}` : 'Judged for another insurance')
	return out
}

/** The `why_not` dictionary with a fallback: never the raw code, never nothing (patterns § 5). */
export function whyNotText(code, { r = null, acriss = '', lor = null, judged = {}, label = null, office = '' } = {}) {
	switch (code) {
		case null:
		case undefined:
			return 'the most specific match'
		case 'outranked':  return 'less specific, not used'
		case 'no_band':    return `“${label || 'the matrix'}” sets no floor for ${acriss || 'this ACRISS'} at ${lor ?? '?'} days`
		case 'offices':    return `not ${office || 'this office'}`
		case 'office_group': return `${office || 'this office'} is not in the group`
		case 'provider':   return judged?.franchise?.asked ? 'another franchise' : 'judged for no franchise'
		case 'prepaid':    return r ? (r.scope_is_prepaid ? 'prepaid only' : 'non-prepaid only') : 'another tariff type'
		case 'insurance':  return 'another insurance'
		case 'categories': return `not ${acriss || 'this ACRISS'}`
		case 'duration':   return `${lor ?? '?'} days is outside its length range`
		case 'dates':      return 'pickup is outside its dates'
		default:           return `did not apply (${code})`
	}
}

/** A protection the rental does not cross: ends before it, or starts after it. */
function datesNote(e, pickup, lastDay) {
	if (e.date_from && lastDay && lastDay < e.date_from) {
		return `this rental ends on the ${ordinal(lastDay)}, not crossed`
	}
	if (e.date_to && pickup && pickup > e.date_to) return 'starts after it'
	return 'not crossed'
}

function ordinal(iso) {
	const d = Number(String(iso).slice(8, 10))
	if (!d) return '?'
	const s = (d % 100 >= 11 && d % 100 <= 13) ? 'th' : ['th', 'st', 'nd', 'rd'][d % 10] || 'th'
	return `${d}${s}`
}
